/*
 * Specializations Section – Cards for each core niche
 * Elderly fitness, Cancer recovery, Mat Pilates, Functional Training
 * Botanical Wellness style with watercolor card illustrations
 */

import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Heart, Sun, Flower2, Dumbbell } from "lucide-react";

const SPEC_ELDERLY = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/WORKINGWITHTHEOLDPEOPLE1_3786b382.jpeg";
const SPEC_CANCER = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/OLDLADYWORKOUTATHOMEPRIVATE2_ee1ab524.jpeg";
const SPEC_PILATES = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/SHARONTRAININGONHERHOME_d4c04028.webp";
const SHARON_TRX = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/פונקציונלי_c930b212.jpeg";

const specializations = [
  {
    icon: Sun,
    title: "כושר לגיל השלישי והרביעי",
    description:
      "אימונים מותאמים אישית לשמירה על עצמאות, שיווי משקל, כוח שרירים וגמישות. עבודה עדינה ומקצועית עם דגש על בטיחות ושמחה בתנועה.",
    image: SPEC_ELDERLY,
    color: "#2D5A3D",
  },
  {
    icon: Heart,
    title: "קהילת מחלימות מסרטן",
    description:
      "ליווי מקצועי ורגיש של נשים בתהליך ההחלמה. אימונים מותאמים שמחזירים את הביטחון בגוף, מחזקים ומעניקים תקווה ואנרגיה חדשה.",
    image: SPEC_CANCER,
    color: "#E8A87C",
  },
  {
    icon: Flower2,
    title: "פילאטיס מזרן",
    description:
      "שיעורי פילאטיס מזרן בקבוצות קטנות ובאימון אישי. חיזוק ליבה, גמישות, יציבה נכונה ומודעות גופנית – בגישה מקצועית ונעימה.",
    image: SPEC_PILATES,
    color: "#2D5A3D",
  },
  {
    icon: Dumbbell,
    title: "אימון פונקציונלי",
    description:
      "אימונים שמכינים את הגוף לחיי היומיום – הרמה, כיפוף, הליכה ושיווי משקל. שילוב של TRX, משקולות, גומיות וכלים נוספים.",
    image: SHARON_TRX,
    color: "#2D5A3D",
  },
];

export default function SpecializationsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="specializations" className="relative py-24 md:py-36 bg-[#FFF8F0]">
      {/* Decorative top wave */}
      <div className="absolute top-0 left-0 right-0 -translate-y-[1px]">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
          <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,20 1440,40 L1440,0 L0,0 Z" fill="#F5F0E8" />
        </svg>
      </div>

      <div className="container relative z-10" ref={ref}>
        {/* Section title */}
        <motion.div
          initial={false}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span
            className="text-[#E8A87C] text-sm font-semibold tracking-wider uppercase mb-3 block"
            style={{ fontFamily: "var(--font-body)" }}
          >
            מה אני עושה
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B4332]"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            תחומי התמחות
          </h2>
          <div className="w-20 h-1 bg-[#E8A87C] mx-auto mt-6 rounded-full" />
        </motion.div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-12">
          {specializations.map((spec, i) => (
            <motion.div
              key={spec.title}
              initial={false}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.15, duration: 0.6 }}
              className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
            >
              {/* Card image */}
              <div className="relative h-56 md:h-64 overflow-hidden">
                <img
                  src={spec.image}
                  alt={spec.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                {/* Icon badge */}
                <div
                  className="absolute bottom-4 right-4 w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: spec.color }}
                >
                  <spec.icon className="w-6 h-6 text-white" />
                </div>
              </div>

              {/* Card content */}
              <div className="p-6 md:p-8">
                <h3
                  className="text-xl md:text-2xl font-bold text-[#1B4332] mb-3"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {spec.title}
                </h3>
                <p
                  className="text-[#3D6B4F] leading-relaxed"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  {spec.description}
                </p>
              </div>

              {/* Hover accent line */}
              <div
                className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-right"
                style={{ backgroundColor: spec.color }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
